/**
 * TTS API服务
 * 提供统一的TTS调用接口，整合单次转换和任务管理功能
 * 
 * 功能特性：
 * - 单次TTS转换
 * - 任务状态管理
 * - 统一的错误处理机制
 * - 统一的数据格式化
 * - 支持多种TTS引擎
 * 
 * @author AI Assistant
 * @since 2025-01-12
 * @version 3.0.0
 */


import { enableLog, formatPrefix } from '@/utils/app-runtime';
import { normalizeTaskStatus } from '@/utils/data-adapter';
import { http } from '@/utils/http';
import { unwrap, rawcall } from '@/utils/apiClient';
import type { Task } from '@/types/task.types';
import type {
  CreateTaskDTO,
  TaskResponseDTO,
  TaskListResponseDTO,
  TaskFilterDTO,
  TaskSortDTO,
  UserTaskLimitsDTO
} from '@/types/task.dto';

// ============= 类型定义 =============

/**
 * TTS参数接口
 */
export interface TTSParams {
  text: string;
  voice: string;
  speed?: number;
  pitch?: number;
  volume?: number;
  format?: string;
  ssml?: string;
  style?: string; // 情感风格
  role?: string; // 角色扮演
  engine?: 'edge' | 'azure' | 'tts88' | 'openai';
  word_boundary?: boolean; // 是否生成字边界（字幕）
  subtitle_format?: 'srt' | 'vtt'; // 字幕格式
}

/**
 * TTS响应接口
 */
export interface TTSResponse {
  taskId: string;
  audioUrl?: string;
  audioBlob?: Blob;
  status: 'created' | 'queued' | 'processing' | 'post_processing' | 'completed' | 'processing_failed' | 'post_failed' | 'failed' | 'cancelled';
  queuePosition?: number;
  estimatedWait?: number;
  userTier?: string;
  priority?: number;
  message?: string;
  createdAt: number;
  progress?: number;
  errorMessage?: string;
}

/**
 * 任务状态接口
 */
export interface TaskStatus {
  taskId: string;
  status: 'created' | 'queued' | 'processing' | 'post_processing' | 'completed' | 'processing_failed' | 'post_failed' | 'failed' | 'cancelled';
  progress: number;
  audioUrl?: string;
  errorMessage?: string;
  queuePosition?: number;
  estimatedWait?: number;
  updatedAt: string;
}

/**
 * TTS历史记录接口
 */
export interface TTSHistory {
  id: string;
  text: string;
  voice: string;
  audioUrl?: string;
  taskId: string;
  status: string;
  createdAt: string;
  fileSize?: number;
  duration?: number;
  engine?: string;
}

// ============= 统一错误处理 =============

/**
 * TTS错误类
 */
export class TTSError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 500,
    public details?: any
  ) {
    super(message);
    this.name = 'TTSError';
  }
}

/**
 * 统一错误处理函数
 */
export function handleTTSError(error: any): never {
  const MODULE_PREFIX = formatPrefix('TTS');
  
  if (error.response) {
    // API响应错误
    const { status, data } = error.response;
    const message = data?.message || data?.error || '请求失败';
    const code = data?.code || 'API_ERROR';
    
    if (enableLog) {
      console.error(`${MODULE_PREFIX} TTS API错误`, { status, message, code, data });
    }
    throw new TTSError(message, code, status, data);
  } else if (error.http) {
    // 网络错误
    if (enableLog) {
      console.error(`${MODULE_PREFIX} TTS网络错误`, { error: error.message });
    }
    throw new TTSError('网络连接失败，请检查网络设置', 'NETWORK_ERROR', 0);
  } else {
    // 其他错误
    if (enableLog) {
      console.error(`${MODULE_PREFIX} TTS未知错误`, { error: error.message });
    }
    throw new TTSError(error.message || '未知错误', 'UNKNOWN_ERROR');
  }
}



// ============= TTS服务类 =============

/**
 * TTS服务
 * 提供完整的TTS功能，整合原有的多个API文件
 */
class TTSService {
  private readonly MODULE_PREFIX = formatPrefix('TTSService');

  /**
   * 处理TTS服务错误
   */
  private handleError(error: any, operation: string): never {
    if (error.response) {
      const { status, data } = error.response;
      const message = data?.message || data?.error || '请求失败';
      const code = data?.code || 'API_ERROR';

      if (enableLog) {
        console.error(`${this.MODULE_PREFIX} TTS ${operation} 失败`, { status, message, code, data });
      }
      throw new TTSError(message, code, status, data);
    } else if (error.http) {
      if (enableLog) {
        console.error(`${this.MODULE_PREFIX} TTS ${operation} 网络错误`, { error: error.message });
      }
      throw new TTSError('网络连接失败，请检查网络设置', 'NETWORK_ERROR', 0);
    } else {
      if (enableLog) {
        console.error(`${this.MODULE_PREFIX} TTS ${operation} 未知错误`, { error: error.message, stack: error.stack });
      }
      throw new TTSError(error.message || '未知错误', 'UNKNOWN_ERROR');
    }
  }


  /**
   * 查询任务状态
   * @param taskId 任务ID
   * @returns 任务状态
   */
  async getTaskStatus(taskId: string): Promise<TaskStatus> {
    try {
      const data = await unwrap<any>(() =>
        http.get(`/api/v1/tts/task/${taskId}/status`)
      );

      return {
        taskId: data.taskId,
        status: normalizeTaskStatus(data.status),
        progress: data.progress || 0,
        audioUrl: data.audioUrl,
        errorMessage: data.error,
        queuePosition: data.queuePosition,
        estimatedWait: data.estimatedWait,
        updatedAt: data.updatedAt || new Date().toISOString()
      };
    } catch (error) {
      this.handleError(error, '查询任务状态');
      throw error;
    }
  }

  /**
   * 获取历史记录
   * @param page 页码
   * @param pageSize 每页大小
   * @returns 历史记录列表
   */
  async getHistory(page: number = 1, pageSize: number = 20): Promise<any> {
    try {
      return await unwrap<any>(() =>
        http.get('/api/v1/tts/history', { params: { page, pageSize } })
      );
    } catch (error) {
      this.handleError(error, '获取历史记录');
      throw error;
    }
  }

  /**
   * 删除历史记录
   * @param historyId 历史记录ID
   */
  async deleteHistory(historyId: string): Promise<void> {
    try {
      await unwrap<void>(() =>
        http.delete(`/api/v1/tts/history/${historyId}`)
      );
    } catch (error) {
      this.handleError(error, '删除历史记录');
      throw error;
    }
  }
}

// ============= 任务管理API类 =============

/**
 * 任务管理API类
 * 提供统一的任务管理功能
 */
export class TaskAPI {
  private baseURL = '/api/v1/tasks'

  /**
   * 创建任务（单次或批量）
   */
  async createTask(data: CreateTaskDTO): Promise<TaskResponseDTO> {
    return unwrap<TaskResponseDTO>(() =>
      http.post(this.baseURL, data)
    )
  }

  /**
   * 获取任务列表
   * @param page 页码，从1开始
   * @param pageSize 每页大小，默认20
   * @param filter 过滤条件
   * @param sort 排序条件
   * @returns Promise<TaskListResponseDTO>
   * @throws Error 当接口调用失败时抛出异常
   */
  async getTasks(
    page: number = 1,
    pageSize: number = 20,
    filter?: TaskFilterDTO,
    sort?: TaskSortDTO
  ): Promise<TaskListResponseDTO> {
    // 使用 camelCase 参数名，符合字段命名规范
    const params = {
      page,
      pageSize, // 使用 camelCase
      ...filter,
      ...(sort && { sortField: sort.field, sortOrder: sort.order }) // 使用 camelCase
    }

    return unwrap<TaskListResponseDTO>(() =>
      http.get(this.baseURL, { params })
    )
  }

  /**
   * 获取单个任务详情
   * @param taskId 任务ID
   * @returns Promise<TaskResponseDTO>
   * @throws Error 当任务不存在或接口调用失败时抛出异常
   */
  async getTask(taskId: string): Promise<TaskResponseDTO> {
    return unwrap<TaskResponseDTO>(() =>
      http.get(`${this.baseURL}/${taskId}`)
    )
  }

  /**
   * 更新任务
   */
  async updateTask(taskId: string, updates: Partial<Task>): Promise<TaskResponseDTO> {
    return unwrap<TaskResponseDTO>(() =>
      http.put(`${this.baseURL}/${taskId}`, updates)
    )
  }

  /**
   * 取消任务
   */
  async cancelTask(taskId: string): Promise<{ success: boolean; message: string }> {
    return unwrap<{ success: boolean; message: string }>(() =>
      http.delete(`${this.baseURL}/${taskId}`)
    )
  }

  /**
   * 重试失败的任务项
   */
  async RetryFailedTaskItems(taskId: string): Promise<TaskResponseDTO> {
    return unwrap<TaskResponseDTO>(() =>
      http.post(`${this.baseURL}/${taskId}/retry`)
    )
  }

  /**
   * 下载任务结果
   * @param taskId 任务ID
   * @param format 下载格式，支持zip和json
   * @returns Promise<Blob | TaskResponseDTO>
   * @throws Error 当任务未完成或下载失败时抛出异常
   */
  async downloadTaskResults(
    taskId: string,
    format: 'json' | 'zip' = 'zip'
  ): Promise<Blob | TaskResponseDTO> {
    if (format === 'zip') {
      // 下载二进制文件，使用 rawcall
      const response = await rawcall<Blob>(() =>
        http.get(`${this.baseURL}/${taskId}/download`, {
          params: { format },
          responseType: 'blob'
        })
      )
      return response.data
    } else {
      // 下载JSON数据，使用 unwrap
      return unwrap<TaskResponseDTO>(() =>
        http.get(`${this.baseURL}/${taskId}/download`, {
          params: { format }
        })
      )
    }
  }

  /**
   * 删除任务
   */
  async deleteTask(taskId: string): Promise<{ success: boolean; message: string }> {
    return unwrap<{ success: boolean; message: string }>(() =>
      http.delete(`${this.baseURL}/${taskId}/delete`)
    )
  }

  /**
   * 批量删除任务
   */
  async deleteTasks(taskIds: string[]): Promise<{ success: boolean; message: string }> {
    return unwrap<{ success: boolean; message: string }>(() =>
      http.post(`${this.baseURL}/batch-delete`, { taskIds })
    )
  }

  /**
   * 获取用户任务权益
   */
  async getUserTaskLimits(): Promise<UserTaskLimitsDTO> {
    return unwrap<UserTaskLimitsDTO>(() =>
      http.get(`${this.baseURL}/limits`)
    )
  }

  /**
   * 获取统一用户权益
   */
  async getUnifiedUserLimits(): Promise<UserTaskLimitsDTO> {
    return unwrap<UserTaskLimitsDTO>(() =>
      http.get(`${this.baseURL}/unified-limits`)
    )
  }

  /**
   * 获取任务统计信息
   */
  async getTaskStats(): Promise<{
    total: number
    completed: number
    failed: number
    processing: number
    todayCreated: number
    todayCompleted: number
  }> {
    return unwrap<{
      total: number
      completed: number
      failed: number
      processing: number
      todayCreated: number
      todayCompleted: number
    }>(() =>
      http.get(`${this.baseURL}/stats`)
    )
  }
}

// ============= 创建实例并导出 =============

// TTS服务实例
const ttsService = new TTSService();

// 任务管理实例
export const taskAPI = new TaskAPI();

// 导出默认TTS服务
export default ttsService;

// ============= 导出便捷函数 =============

// TTS相关函数
export const getTaskStatus = (taskId: string) => ttsService.getTaskStatus(taskId);
export const getHistory = (page?: number, pageSize?: number) => ttsService.getHistory(page, pageSize);
export const deleteHistory = (historyId: string) => ttsService.deleteHistory(historyId);

// 任务管理相关函数
export const createTask = (data: CreateTaskDTO) => taskAPI.createTask(data);
export const getTasks = (page?: number, pageSize?: number, filter?: TaskFilterDTO, sort?: TaskSortDTO) =>
  taskAPI.getTasks(page, pageSize, filter, sort);
export const getTask = (taskId: string) => taskAPI.getTask(taskId);
export const updateTask = (taskId: string, updates: Partial<Task>) => taskAPI.updateTask(taskId, updates);
export const cancelTask = (taskId: string) => taskAPI.cancelTask(taskId);
export const RetryFailedTaskItems = (taskId: string) => taskAPI.RetryFailedTaskItems(taskId);
export const downloadTaskResults = (taskId: string, format?: 'json' | 'zip') =>
  taskAPI.downloadTaskResults(taskId, format);
export const deleteTask = (taskId: string) => taskAPI.deleteTask(taskId);
export const deleteTasks = (taskIds: string[]) => taskAPI.deleteTasks(taskIds);
export const getUserTaskLimits = () => taskAPI.getUserTaskLimits();
export const getUnifiedUserLimits = () => taskAPI.getUnifiedUserLimits();
export const getTaskStats = () => taskAPI.getTaskStats();

// ============= 工具函数 =============

/**
 * 生成任务ID
 */
export function generateTaskId(): string {
  return `task_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * 计算任务进度
 */
export function calculateTaskProgress(task: Task): number {
  if (task.type === 'single') {
    return task.progress;
  }
  // BatchTask
  if (task.totalItems === 0) return 0;
  return Math.round(((task.completedItems || 0) / (task.totalItems || 1)) * 100);
}

// ============= 格式化函数已移至 @/utils/formatters.ts =============
// 请直接从 @/utils/formatters 导入:
// import {
//   formatDurationChinese,
//   formatFileSize,
//   getTaskStatusText,
//   getTaskTypeText
// } from '@/utils/formatters';


